import React, { useEffect, useState } from "react";

// Next
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";

// React Hot Toast
import { Toaster } from "react-hot-toast";

// Hotkeys
import hotkeys from "hotkeys-js";

// Components
import Navbar from "../Navbar";
import Footer from "../Footer";
import SidebarMenu from "../SidebarMenu";

// Utils
import { useAppDispatch } from "@/utils/hooks/store";
import { setUser } from "@/store/slices/authSlice";
import HelpModal from "@/containers/Modals/HelpModal";

type LayoutProps = {
  children: React.ReactNode;
  sidebarMenu?: boolean;
};

const Layout: React.FC<LayoutProps> = ({ children, sidebarMenu = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const dispatch = useAppDispatch();

  const router = useRouter();
  const locale = useLocale();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const username = localStorage.getItem("username") || "";
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    dispatch(
      setUser({
        username,
        roles,
      })
    );
  }, []);

  useEffect(() => {
    hotkeys("alt+h", (event) => {
      event.preventDefault();
      router.push(`/${locale}`);
    });

    hotkeys("alt+backspace", (event) => {
      event.preventDefault();
      router.back();
    });

    hotkeys("alt+shift+n", (event) => {
      event.preventDefault();
      router.forward();
    });

    hotkeys("alt+?,alt+/", (event) => {
      event.preventDefault();
      setHelpModalOpen(true);
    });

    return () => {
      hotkeys.unbind("alt+h");
      hotkeys.unbind("alt+backspace");
      hotkeys.unbind("alt+shift+n");
      hotkeys.unbind("alt+?,alt+/");
    };
  }, [router, locale]);

  return (
    <main className="flex min-h-screen h-full min-w-screen w-full dark:bg-dark-mode">
      {sidebarMenu && (
        <SidebarMenu isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      )}

      {helpModalOpen && (
        <HelpModal
          isOpen={helpModalOpen}
          onClose={() => setHelpModalOpen(false)}
        />
      )}

      <Toaster />

      <section className="w-full">
        <Navbar
          fqcn_bui={{
            Bundle: "layoutBundle",
            Unit: "navbar",
            Interface: "layout",
          }}
          sidebarMenu={sidebarMenu}
          setSidebarOpen={setSidebarOpen}
        />

        <div
          className={`page-height relative mx-auto ${
            sidebarMenu && sidebarOpen ? "md:max-w-[calc(100vw-220px)]" : ""
          }`}
        >
          {children}
        </div>

        <Footer
          fqcn_bui={{
            Bundle: "layoutBundle",
            Unit: "navbar",
            Interface: "layout",
          }}
        />
      </section>
    </main>
  );
};

export default Layout;
